import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useState, useMemo } from "react";
import { useAbbreviationsCache, useSettingsCache } from "@/shared/cache";
import { Search, Plus, Trash2 } from "lucide-react";
import { Input } from "@/v2/components/ui/input";
import { Button } from "@/v2/components/ui/button";
import { Switch } from "@/v2/components/ui/switch";
import { SettingRow } from "@/v2/components/settings/SettingRow";
import { SettingGroup } from "@/v2/components/settings/SettingGroup";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { toast } from "sonner";
import {
	ABBREVIATION_TRIGGER_ERROR,
	isValidAbbreviationTrigger,
	normalizeAbbreviationTrigger,
} from "@/shared/utils/abbreviations";

export default function Abbreviations() {
	useDocumentTitle("Abbreviations");
	const {
		enabled,
		abbreviations,
		isLoading,
		setLocalEnabled,
		createAbbreviation,
		deleteAbbreviation,
	} = useAbbreviationsCache();
	const { updateSettings } = useSettingsCache();

	const [search, setSearch] = useState("");
	const [newAbbr, setNewAbbr] = useState("");
	const [newExpansion, setNewExpansion] = useState("");
	const [adding, setAdding] = useState(false);

	const filtered = useMemo(() => {
		if (!search) return abbreviations;
		const q = search.toLowerCase();
		return abbreviations.filter(
			(a) =>
				a.abbreviation.toLowerCase().includes(q) ||
				a.expansion.toLowerCase().includes(q),
		);
	}, [abbreviations, search]);

	const handleToggleEnabled = async (value: boolean) => {
		setLocalEnabled(value);
		await updateSettings({ abbreviations_enabled: value });
	};

	const handleCreate = async () => {
		const abbr = normalizeAbbreviationTrigger(newAbbr);
		const exp = newExpansion.trim();
		if (!abbr || !exp) return;

		if (abbr.length < 2) {
			toast.error("Abbreviation must be at least 2 characters");
			return;
		}
		if (abbr.length > 50) {
			toast.error("Abbreviation must be 50 characters or less");
			return;
		}
		if (!isValidAbbreviationTrigger(abbr)) {
			toast.error(ABBREVIATION_TRIGGER_ERROR);
			return;
		}
		if (exp.length > 500) {
			toast.error("Expansion must be 500 characters or less");
			return;
		}
		// Best-effort guard against the cached list — avoids a round-trip in the
		// common case. If the cache is stale (e.g. another tab just added the same
		// trigger), the API's own P2002 handler returns 409 and result.error below
		// surfaces the same message, so the user always gets correct feedback.
		if (abbreviations.some((a) => a.abbreviation === abbr)) {
			toast.error(`Abbreviation "${abbr}" already exists`);
			return;
		}

		setAdding(true);
		const result = await createAbbreviation(abbr, exp);
		if (result.ok) {
			setNewAbbr("");
			setNewExpansion("");
		} else {
			toast.error(result.error ?? "Failed to create abbreviation");
		}
		setAdding(false);
	};

	const handleDelete = async (id: number) => {
		const result = await deleteAbbreviation(id);
		if (!result.ok) {
			toast.error(result.error ?? "Failed to delete abbreviation");
		}
	};

	if (isLoading) {
		return (
			<div>
				<h2 className="text-lg font-semibold mb-4">Abbreviations</h2>
				<div className="space-y-3">
					<div className="h-10 rounded-lg bg-muted animate-pulse" />
					<div className="h-10 rounded-lg bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">Abbreviations</h2>

			<SettingGroup>
				<SettingRow
					title="Enable abbreviations"
					description="Automatically expand abbreviations while typing"
				>
					<Switch
						checked={enabled}
						onCheckedChange={handleToggleEnabled}
					/>
				</SettingRow>
			</SettingGroup>

			<SectionHeading>Add New</SectionHeading>
			<div className="flex flex-col sm:flex-row sm:items-end gap-2 py-2">
				<div className="sm:flex-1 space-y-1">
					<label className="text-xs text-muted-foreground">
						Abbreviation
					</label>
					<Input
						value={newAbbr}
						onChange={(e) => setNewAbbr(e.target.value)}
						maxLength={50}
						placeholder="e.g. btw"
						className="h-9"
					/>
				</div>
				<div className="flex items-end gap-2 sm:flex-1">
					<div className="flex-1 space-y-1">
						<label className="text-xs text-muted-foreground">
							Expansion
						</label>
						<Input
							value={newExpansion}
							onChange={(e) => setNewExpansion(e.target.value)}
							placeholder="e.g. by the way"
							className="h-9"
							onKeyDown={(e) => {
								if (e.key === "Enter") handleCreate();
							}}
						/>
					</div>
					<Button
						size="sm"
						onClick={handleCreate}
						disabled={
							adding ||
							!newAbbr.trim() ||
							!newExpansion.trim()
						}
						loading={adding}
					>
						<Plus className="size-4" />
					</Button>
				</div>
			</div>

			<SectionHeading>
				{abbreviations.length} abbreviation
				{abbreviations.length !== 1 && "s"}
			</SectionHeading>

			{/* Search */}
			<div className="relative mb-2">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
				<Input
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Search abbreviations..."
					className="pl-9 h-9"
				/>
			</div>

			{filtered.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{search
						? "No abbreviations match your search."
						: "No abbreviations yet."}
				</p>
			) : (
				<SettingGroup card>
					{filtered.map((a) => (
						<div
							key={a.id}
							className="flex items-center gap-3 px-3 py-2 sm:px-4 sm:py-3"
						>
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium font-mono">
									{a.abbreviation}
								</p>
								<p className="text-sm text-muted-foreground truncate">
									{a.expansion}
								</p>
							</div>
							<button
								onClick={() => handleDelete(a.id)}
								className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
							>
								<Trash2 className="size-4" />
							</button>
						</div>
					))}
				</SettingGroup>
			)}
		</div>
	);
}
